import { CheckedState } from "@radix-ui/react-checkbox"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"

import { ListItemStack } from "@molecules"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"

import { Settings } from "@templates/index"

import {
    EstimatesNotificationTopics,
    NotificationMethods,
} from "@pages/Settings/SettingsNotifications/SettingsNotification.types"
import {
    notificationPreferenceQueryKey,
    useNotificationPreferencesQuery,
} from "@pages/Settings/SettingsNotifications/SettingsNotifications.store"
import NotificationProperty from "@pages/Settings/SettingsNotifications/components/NotificationProperty/NotificationProperty"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

export default function EstimatesNotificationsForm() {
    const [shouldEnableAllEmailNotifications, setShouldEnableAllEmailNotifications] = useState<boolean>(false)
    const [shouldDisableAllEmailNotifications, setShouldDisableAllEmailNotifications] = useState<boolean>(false)
    const [shouldEnableAllSmsNotifications, setShouldEnableAllSmsNotifications] = useState<boolean>(false)
    const [shouldDisableAllSmsNotifications, setShouldDisableAllSmsNotifications] = useState<boolean>(false)
    const [shouldSetDefaultFormData, setShouldSetDefaultFormData] = useState<boolean>(false)

    const { topicPreference: estimatesNotificationPreference, isLoading: isLoadingData } =
        useNotificationPreferencesQuery<EstimatesNotificationTopics>("estimates")

    const queryClient = useQueryClient()

    useEffect(() => {
        if (!isLoadingData && !!estimatesNotificationPreference) {
            setShouldSetDefaultFormData(true)
        }
    }, [isLoadingData])

    const handleToggleAllNotifications = (method: NotificationMethods, shouldEnableAllNotifications: CheckedState) => {
        if (method === "email") {
            setShouldDisableAllEmailNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllEmailNotifications(!!shouldEnableAllNotifications)
        } else {
            setShouldDisableAllSmsNotifications(!shouldEnableAllNotifications)
            setShouldEnableAllSmsNotifications(!!shouldEnableAllNotifications)
        }
    }

    const modifyFieldset = (fieldset: EstimatesNotificationTopics, method: NotificationMethods, value: boolean) => {
        return Object.keys(fieldset).reduce((acc, key) => {
            const notificationKey = key as keyof EstimatesNotificationTopics
            if (notificationKey.endsWith(`_${method}`)) {
                acc[notificationKey] = value
            }
            return acc
        }, fieldset)
    }

    const formValues: EstimatesNotificationTopics | undefined = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "sms", false)
        } else if (shouldSetDefaultFormData) {
            return estimatesNotificationPreference
        } else {
            return undefined
        }
    }, [
        shouldSetDefaultFormData,
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const mutationTriggerPayload = useMemo(() => {
        if (shouldEnableAllEmailNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "email", true)
        } else if (shouldEnableAllSmsNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "sms", true)
        } else if (shouldDisableAllEmailNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "email", false)
        } else if (shouldDisableAllSmsNotifications) {
            return modifyFieldset(estimatesNotificationPreference, "sms", false)
        } else {
            return undefined
        }
    }, [
        shouldEnableAllEmailNotifications,
        shouldDisableAllEmailNotifications,
        shouldEnableAllSmsNotifications,
        shouldDisableAllSmsNotifications,
    ])

    const resetNotificationStates = () => {
        setShouldEnableAllEmailNotifications(false)
        setShouldDisableAllEmailNotifications(false)
        setShouldEnableAllSmsNotifications(false)
        setShouldDisableAllSmsNotifications(false)
    }

    const onMutationSuccess = () => {
        void queryClient.invalidateQueries({ queryKey: notificationPreferenceQueryKey })
    }

    const onAfterFormValuesSet = () => {
        resetNotificationStates()
        setShouldSetDefaultFormData(false)
    }

    return (
        <GenericForm<EstimatesNotificationTopics, EstimatesNotificationTopics, Error, EstimatesNotificationTopics>
            formConfig={{
                defaultValues: undefined,
            }}
            mutationConfig={{
                genericErrorMessage: "Could not update notification preference",
                genericSuccessMessage: "Notification preference updated",
                method: "PATCH",
                endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES,
            }}
            onMutationSuccess={onMutationSuccess}
            mutationTriggerPayload={mutationTriggerPayload}
            formValues={formValues}
            onAfterFormValuesSet={onAfterFormValuesSet}
            isAutoSave={true}
        >
            {({ form }) => {
                const formValues = form.getValues()

                const emailValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_email"))
                    .map(([, value]) => value)

                const smsValues = Object.entries(formValues)
                    .filter(([key]) => key.endsWith("_sms"))
                    .map(([, value]) => value)

                const isAllNotificationsEmailEnabled = emailValues.every(Boolean)
                const isAllNotificationsSmsEnabled = smsValues.every(Boolean)
                const isSomeNotificationsEmailEnabled = emailValues.some(Boolean)
                const isSomeNotificationsSmsEnabled = smsValues.some(Boolean)

                return (
                    <Settings.NotificationConfigPage.Wrapper>
                        <ListItemStack>
                            <NotificationProperty
                                label="Enable general notifications"
                                isLoading={isLoadingData}
                                isControlled={false}
                                isEmailIndeterminate={
                                    isSomeNotificationsEmailEnabled && !isAllNotificationsEmailEnabled
                                }
                                isEmailChecked={isAllNotificationsEmailEnabled}
                                onEmailCheckedChange={(checked) => handleToggleAllNotifications("email", checked)}
                                isSmsIndeterminate={isSomeNotificationsSmsEnabled && !isAllNotificationsSmsEnabled}
                                isSmsChecked={isAllNotificationsSmsEnabled}
                                onSmsCheckedChange={(checked) => handleToggleAllNotifications("sms", checked)}
                            />
                        </ListItemStack>
                        <ListItemStack>
                            <NotificationProperty<EstimatesNotificationTopics>
                                label="Estimate approved"
                                supportText="Notify me when a client approves an estimate."
                                emailCheckboxName="enable_estimate_notification_estimate_approved_email"
                                smsCheckboxName="enable_estimate_notification_estimate_approved_sms"
                                isLoading={isLoadingData}
                            />
                            <ListItemDivider />
                            <NotificationProperty<EstimatesNotificationTopics>
                                label="Estimate declined"
                                supportText="Notify me when a client declines an estimate."
                                emailCheckboxName="enable_estimate_notification_estimate_declined_email"
                                smsCheckboxName="enable_estimate_notification_estimate_declined_sms"
                                isLoading={isLoadingData}
                            />
                        </ListItemStack>
                    </Settings.NotificationConfigPage.Wrapper>
                )
            }}
        </GenericForm>
    )
}
